import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Sparkles, ShieldCheck, ArrowRight, Zap, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { FR_CALCULATOR_CATEGORIES, FR_CALCULATORS, type FrCalculatorCategory } from "@/lib/fr-calculators-catalog";
import { CalcIcon } from "@/components/calculators/CalcIcon";

export const Route = createFileRoute("/fr/calculators/")({
  head: () => ({
    meta: [
      { title: "Calculateurs en Ligne Gratuits — 122+ Outils Maths, Finance & Santé | Picamini.online" },
      {
        name: "description",
        content:
          "Calculateurs en ligne gratuits pour étudiants, enseignants et professionnels. Calculateur de pourcentage, prêt immobilier, IMC, moyenne et outils de maths 100% privés.",
      },
      {
        name: "keywords",
        content:
          "calculateur en ligne, calculateur de pourcentage, calculateur de moyenne, calculateur imc, simulateur pret immobilier",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Calculateurs en Ligne Gratuits — 122+ Outils Instantanés" },
      {
        property: "og:description",
        content:
          "Calculateurs en ligne rapides, précis et 100% privés. Effectuez vos calculs mathématiques, financiers et santé dans votre navigateur.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/fr/calculators" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "fr_FR" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Calculateurs en Ligne Gratuits — 122+ Outils" },
      { name: "twitter:description", content: "Calculateurs gratuits en ligne pour les mathématiques, la finance, le fitness et la physique." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/fr/calculators" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/calculators" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/calculators" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/calculators" },
      { rel: "alternate", hrefLang: "fr", href: "https://picamini.online/fr/calculators" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/calculators" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/calculators" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Répertoire de calculateurs en ligne gratuits",
          "url": "https://picamini.online/fr/calculators",
          "inLanguage": "fr",
          "description": "Répertoire de 122+ calculateurs gratuits et privés en ligne pour les maths, les finances et la santé.",
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": FR_CALCULATORS.length,
            "itemListElement": FR_CALCULATORS.slice(0, 20).map((c, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": c.name,
              "url": `https://picamini.online/fr/calculators/${c.slug}`,
              "description": c.tagline
            }))
          }
        })
      }
    ]
  }),
  component: FrCalculatorsHub,
});

export function FrCalculatorsHub() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<FrCalculatorCategory | "Tous">("Tous");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FR_CALCULATORS.filter((c) => {
      const matchesCategory = category === "Tous" || c.category === category;
      const matchesQuery =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.tagline.toLowerCase().includes(q) ||
        c.keywords.some((k) => k.includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader lang="fr" />
      <main>
        {/* Hero Section */}
        <section className="bg-hero-gradient text-primary-foreground">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur mb-4">
              <ShieldCheck className="size-4 text-emerald-300" />
              100% Gratuit & Privé — Calculs Instantanés dans le Navigateur
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-5xl lg:text-6xl">
              Calculateurs en Ligne Gratuits
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-primary-foreground/80 leading-relaxed">
              Des outils rapides, fiables et intuitifs pour étudiants, enseignants et calculs du quotidien.
              Calculez pourcentages, moyennes, mensualités de prêt, IMC et plus en un clic.
            </p>

            {/* Live Search Bar */}
            <div className="mx-auto mt-8 max-w-xl">
              <div className="relative flex items-center">
                <Search className="absolute left-4 size-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Rechercher un calculateur (ex. pourcentage, moyenne, prêt)..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-13 pl-12 pr-4 rounded-2xl bg-background/95 text-foreground shadow-lg backdrop-blur text-sm sm:text-base border-white/20 focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Catalog Section */}
        <section className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-12">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border/80 pb-4">
            <Button
              variant={category === "Tous" ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory("Tous")}
              className="rounded-full text-xs font-bold"
            >
              Tous ({FR_CALCULATORS.length})
            </Button>
            {FR_CALCULATOR_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                variant={category === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(cat)}
                className="rounded-full text-xs font-bold"
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Calculator Grid */}
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((calc) => (
              <Link
                key={calc.slug}
                to={`/fr/calculators/${calc.slug}`}
                className="group relative flex flex-col justify-between rounded-3xl border border-border/80 bg-card p-6 shadow-xs transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-xl"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <CalcIcon name={calc.icon} className="size-6" />
                    </div>
                    {calc.featured && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-amber-600 dark:text-amber-400 border border-amber-500/20">
                        <Sparkles className="size-3" /> En Vedette
                      </span>
                    )}
                  </div>

                  <h3 className="mt-5 text-xl font-bold text-foreground transition-colors group-hover:text-primary">
                    {calc.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {calc.tagline}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4 text-xs font-bold text-primary">
                  <span>Ouvrir le calculateur</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg font-bold text-foreground">Aucun calculateur trouvé</p>
              <p className="text-sm text-muted-foreground mt-1">Essayez avec d'autres mots-clés ou choisissez une autre catégorie.</p>
            </div>
          )}
        </section>

        {/* Informational Section */}
        <section className="border-t border-border/60 bg-secondary/15 py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-12">
              <header className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Outils de Calcul Précis, Gratuits et Sécurisés
                </span>
                <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-foreground">
                  La Suite Complète de Calculateurs en Ligne
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Picamini.online met à votre disposition <strong>122+ calculateurs gratuits</strong> pour résoudre des équations, simuler des emprunts, suivre vos progrès sportifs et calculer vos moyennes en toute confidentialité.
                </p>
              </header>

              <div className="grid gap-6 sm:grid-cols-3 not-prose">
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <ShieldCheck className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">100% Privé</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Tous les calculs sont traités directement dans votre navigateur sans transfert de données.</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <Zap className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Résultats Instantanés</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Mise à jour automatique des formules en temps réel avec explications étape par étape.</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Sans Inscription</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Accès illimité sans compte, sans carte bancaire ni publicités intrusives.</p>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>
      <SiteFooter lang="fr" />
    </div>
  );
}
